// Install support. Chrome fires beforeinstallprompt once, early — before the
// settings tab is ever opened — so main.tsx captures it here and the install
// card in הגדרות subscribes. iOS/Safari never fires it (install is "הוספה למסך הבית"
// from the share sheet), so the entry stays hidden there.
export type InstallEvent = Event & { prompt: () => Promise<void>; userChoice: Promise<{ outcome: string }> };

let deferred: InstallEvent | null = null;
const listeners = new Set<(e: InstallEvent | null) => void>();

function set(e: InstallEvent | null) {
  deferred = e;
  for (const fn of listeners) fn(e);
}

export function installEvent(): InstallEvent | null {
  return deferred;
}

export function onInstallChange(fn: (e: InstallEvent | null) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Registers the service worker and starts listening; returns a cleanup fn. */
export function startPwa(): () => void {
  if (typeof window === "undefined") return () => {};
  if ("serviceWorker" in navigator) navigator.serviceWorker.register("/sw.js").catch(() => {});
  const onPrompt = (e: Event) => { e.preventDefault(); set(e as InstallEvent); };
  const onInstalled = () => set(null);
  window.addEventListener("beforeinstallprompt", onPrompt);
  window.addEventListener("appinstalled", onInstalled);
  return () => {
    window.removeEventListener("beforeinstallprompt", onPrompt);
    window.removeEventListener("appinstalled", onInstalled);
  };
}

/** True when the app is running as an installed PWA. */
export function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(display-mode: standalone)").matches || (navigator as unknown as { standalone?: boolean }).standalone === true;
}
